import { Button, FormGroup, Input, Label, Select } from '@/components'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { useRedis } from '@/hooks/useRedis'
import commonUtils from '@/utils/commonUtils'
import { useEffect, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

interface DataRow {
  locdate: string
  dateName: string
  isHoliday: string
  dateKind: string
  batchYn: string
  useYn: string
  regId: string
  regDate: string
  updId: string
  updDate: string
}

const HolidayMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const [data, setData] = useState<DataRow[]>([])
  const { getComCodeValue } = useRedis()
  const { showAlert } = useDialog()

  const [srchData, setSrchData] = useState({
    locdate: '',
    dateName: '',
    batchYn: '',
    useYn: '',
    isHoliday: '',
    startDate: '',
    endDate: '',
  })

  const [currentPage, setCurrentPage] = useState(location.state?.page || 1)
  const [perPage, setPerPage] = useState(location.state?.perPage || 10)

  useEffect(() => {
    const prevState = location.state?.prevSearchState
    if (prevState) {
      setSrchData(prevState)
      setCurrentPage(prevState.page)
      setPerPage(prevState.perPage)
    }
    getHolidayInfo()
  }, [])

  const columns: TableColumn<DataRow>[] = [
    {
      name: '날짜',
      selector: (row) => commonUtils.formatDate(row.locdate),
      sortable: true,
      wrap: true,
      center: true,
    },
    {
      name: '명칭',
      selector: (row) => row.dateName,
      sortable: true,
      wrap: true,
    },
    {
      name: '휴일여부',
      selector: (row) => row.isHoliday,
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '특일정보',
      selector: (row) =>
        getComCodeValue('DATE_KIND', row.dateKind)?.codeNm || '',
      sortable: true,
      wrap: true,
    },
    {
      name: '배치여부',
      selector: (row) => row.batchYn,
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '사용여부',
      selector: (row) => row.useYn,
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '수정자',
      selector: (row) => row.updId,
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '수정일',
      selector: (row) => row.updDate,
      format: (row) => commonUtils.formatDate(row.updDate, false),
      sortable: true,
      wrap: true,
      center: true,
    },
    {
      name: '',
      cell: (row) => (
        <>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonUpdateClick(row.locdate)}
          >
            수정
          </Button>
        </>
      ),
      center: true,
      sortable: false,
    },
  ]

  const handleSearch = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement // 타입 캐스팅
    setSrchData((prev) => ({ ...prev, [name]: value }))
  }

  const handleButtonUpdateClick = async (locdate: string) => {
    navigate(`/holiday/detail`, {
      state: {
        prevSearchState: {
          ...srchData,
          results: data,
          page: currentPage,
          perPage: perPage,
        },
        locdate: locdate,
      },
    })
  }

  const isValidDate = (dateString: string): boolean => {
    if (!/^\d{8}$/.test(dateString)) return false

    const year = parseInt(dateString.substring(0, 4), 10)
    const month = parseInt(dateString.substring(4, 6), 10)
    const day = parseInt(dateString.substring(6, 8), 10)

    const date = new Date(year, month - 1, day)
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    )
  }

  const getHolidayInfo = async () => {
    try {
      const params: any = {}
      if (srchData.locdate.trim()) {
        params.locdate = srchData.locdate
        if (!isValidDate(params.locdate)) {
          showAlert({
            type: 'warning',
            title:
              '날짜 형식이 올바르지 않습니다. YYYYMMDD 형식으로 입력해주세요.',
          })
          return false
        }
      }
      if (srchData.dateName.trim()) {
        params.dateName = srchData.dateName
      }
      if (srchData.batchYn.trim()) {
        params.batchYn = srchData.batchYn
      }
      if (srchData.isHoliday.trim()) {
        params.isHoliday = srchData.isHoliday
      }
      if (srchData.useYn.trim()) {
        params.useYn = srchData.useYn
      }
      if (srchData.startDate.trim()) {
        params.startDate = srchData.startDate
        if (!isValidDate(params.startDate)) {
          showAlert({
            type: 'warning',
            title:
              '날짜 형식이 올바르지 않습니다. YYYYMMDD 형식으로 입력해주세요.',
          })
          return false
        }
      }

      if (srchData.endDate.trim()) {
        params.endDate = srchData.endDate
        if (!isValidDate(params.endDate)) {
          showAlert({
            type: 'warning',
            title:
              '날짜 형식이 올바르지 않습니다. YYYYMMDD 형식으로 입력해주세요.',
          })
          return false
        }
      }

      const response = await axios.get('/holiday/getInfo', { params })

      const data = response.data
      if (data.common.resultCode === 'HOLIDAY0200') {
        const listData = data.data.holidayInfo
        if (Array.isArray(listData)) {
          setData(listData)
        }
      } else {
        showAlert({
          title:
            data.common.resultMsg ||
            '날짜 형식이 올바르지 않습니다. YYYYMMDD 형식으로 입력해주세요.',
        })
      }
    } catch (error) {
      showAlert({
        title: '오류가 발생했어요. 잠시 후 다시 시도해 주세요.',
        message: error,
      })
      console.error('Error fetching getHolidayInfo :', error)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      getHolidayInfo()
    }
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>휴일관리</p>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '5px' }}>
            <FormGroup>
              <Label htmlFor={'dateName'}>명칭</Label>
              <FlexWrapper gap="10px">
                <Input
                  placeholder="명칭"
                  width="120px"
                  id="dateName"
                  name="dateName"
                  maxLength={50}
                  value={srchData.dateName}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'startDate'}>기간</Label>
              <FlexWrapper gap="10px">
                <Input
                  placeholder="YYYYMMDD"
                  width="120px"
                  id="startDate"
                  name="startDate"
                  maxLength={8}
                  value={srchData.startDate}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
                <>~</>
                <Input
                  placeholder="YYYYMMDD"
                  width="120px"
                  id="endDate"
                  name="endDate"
                  maxLength={8}
                  value={srchData.endDate}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'isHoliday'}>휴일여부</Label>
              <FlexWrapper gap="10px">
                <Select
                  id="isHoliday"
                  name="isHoliday"
                  width="100px"
                  onChange={handleSearch}
                >
                  <option value="">전체</option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </Select>
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'batchYn'}>배치여부</Label>
              <FlexWrapper gap="10px">
                <Select
                  id="batchYn"
                  name="batchYn"
                  width="100px"
                  onChange={handleSearch}
                >
                  <option value="">전체</option>
                  <option value="Y">배치</option>
                  <option value="N">수기관리</option>
                </Select>
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'useYn'}>사용여부</Label>
              <FlexWrapper gap="10px">
                <Select
                  id="useYn"
                  name="useYn"
                  width="100px"
                  onChange={handleSearch}
                >
                  <option value="">전체</option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </Select>
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Button
                  onClick={getHolidayInfo}
                  color="neverbe"
                  variant="outlined"
                >
                  검색
                </Button>
                <Button
                  onClick={() => {
                    navigate('/holiday/detail', {
                      state: {
                        page: currentPage,
                        perPage: perPage,
                      },
                    })
                  }}
                  color="neverbe"
                >
                  등록
                </Button>
              </FlexWrapper>
            </FormGroup>
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={data}
        paginationDefaultPage={currentPage}
        onChangePage={(page) => setCurrentPage(page)}
        pagination={false}
        paginationPerPage={perPage}
        onChangeRowsPerPage={(page) => setPerPage(page)}
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
        noDataComponent={<>조회결과가 없습니다.</>}
      />
    </>
  )
}
export default HolidayMgmt
